import { z } from 'zod';

import { endpoint } from 'services/api/endpoint';

import {
  BankFeedConnectionAccountSchema,
  BankFeedConnectionSchema,
  BankFeedExportPathSchema,
  BankFeedExportStatusResponseSchema,
  BankFeedSummarySchema,
  BankFeedSyncSettingsSchema,
  BankFeedTransactionsExportBodySchema,
  BankFeedTransactionsExportResponseSchema,
  BankProvidersResponseSchema,
  ConnectionCreateBodySchema,
  ConnectionCreateResponseSchema,
  ConnectionIdPathSchema,
  ConnectionsByIdsBodySchema,
  ConnectionsByIdsResponseSchema,
  ConnectionsGridResponseSchema,
  ConnectionUpdateBodySchema,
  ErpAccountsMatchBodySchema,
  ErpAccountsQuerySchema,
  ErpAccountsResponseSchema,
  SyncSchedulesPathSchema,
  TransactionsAutocompleteBodySchema,
  TransactionsAutocompleteResponseSchema,
  TransactionsGridHeadersQuerySchema,
  TransactionsGridHeadersResponseSchema,
  TransactionsGridSearchBodySchema,
  BankFeedTransactionsGridResponseSchema,
  UserPermissionsGridResponseSchema,
  UserPermissionsUpdateBodySchema,
  UserPermissionsUpdateResponseSchema,
} from 'domain/bankFeed/schemas';

export const bankFeedApi = {
  // ── reads ───────────────────────────────────────────────────────────────
  getConnections: endpoint({
    method: 'GET',
    url: 'bank-feed/connections/grid',
    response: ConnectionsGridResponseSchema,
  }),

  getProviders: endpoint({
    method: 'GET',
    url: 'bank-feed/providers',
    response: BankProvidersResponseSchema,
  }),

  getErpAccounts: endpoint({
    method: 'GET',
    url: 'bank-feed/erp-accounts',
    query: ErpAccountsQuerySchema,
    response: ErpAccountsResponseSchema,
  }),

  getUserPermissions: endpoint({
    method: 'GET',
    url: 'bank-feed/company/permissions',
    response: UserPermissionsGridResponseSchema,
  }),

  getSyncSchedules: endpoint({
    method: 'GET',
    url: (p: { companyId: string }) => `preferences/company/${p.companyId}/bank-feed`,
    path: SyncSchedulesPathSchema,
    response: BankFeedSyncSettingsSchema,
  }),

  getConnectionsByIds: endpoint({
    method: 'POST',
    url: 'bank-feed/connections/grid/search',
    body: ConnectionsByIdsBodySchema,
    response: ConnectionsByIdsResponseSchema,
  }),

  getTransactionsAutocomplete: endpoint({
    method: 'POST',
    url: 'bank-feed/transactions/grid/autocomplete',
    body: TransactionsAutocompleteBodySchema,
    response: TransactionsAutocompleteResponseSchema,
  }),

  getTransactionsGridHeaders: endpoint({
    method: 'GET',
    url: 'bank-feed/transactions/grid/headers',
    query: TransactionsGridHeadersQuerySchema,
    response: TransactionsGridHeadersResponseSchema,
  }),

  getTransactionsGridSummary: endpoint({
    method: 'GET',
    url: 'bank-feed/transactions/grid/summary',
    response: BankFeedSummarySchema,
  }),

  getTransactionsGridSearch: endpoint({
    method: 'POST',
    url: 'bank-feed/transactions/grid/search',
    body: TransactionsGridSearchBodySchema,
    response: BankFeedTransactionsGridResponseSchema,
  }),

  getExport: endpoint({
    method: 'GET',
    url: (p: { exportId: string }) => `bank-feed/exports/${p.exportId}`,
    path: BankFeedExportPathSchema,
    response: BankFeedExportStatusResponseSchema,
  }),

  // ── writes ──────────────────────────────────────────────────────────────
  createConnection: endpoint({
    method: 'POST',
    url: 'bank-feed/connections',
    body: ConnectionCreateBodySchema,
    response: ConnectionCreateResponseSchema,
  }),

  updateConnection: endpoint({
    method: 'PATCH',
    url: (p: { id: string }) => `bank-feed/connections/${p.id}`,
    path: ConnectionIdPathSchema,
    body: ConnectionUpdateBodySchema,
    response: BankFeedConnectionSchema,
  }),

  deleteConnection: endpoint({
    method: 'DELETE',
    url: (p: { id: string }) => `bank-feed/connections/${p.id}`,
    path: ConnectionIdPathSchema,
    response: z.unknown(),
  }),

  syncTransactions: endpoint({
    method: 'POST',
    url: (p: { id: string }) => `bank-feed/connections/${p.id}/transactions/sync`,
    path: ConnectionIdPathSchema,
    response: BankFeedConnectionSchema,
  }),

  matchErpAccount: endpoint({
    method: 'POST',
    url: 'bank-feed/erp-accounts/match',
    body: ErpAccountsMatchBodySchema,
    response: BankFeedConnectionAccountSchema,
  }),

  updateUserPermissions: endpoint({
    method: 'POST',
    url: 'bank-feed/company/permissions',
    body: UserPermissionsUpdateBodySchema,
    response: UserPermissionsUpdateResponseSchema,
  }),

  updateSyncSchedules: endpoint({
    method: 'POST',
    url: (p: { companyId: string }) => `preferences/company/${p.companyId}/bank-feed`,
    path: SyncSchedulesPathSchema,
    body: BankFeedSyncSettingsSchema,
    response: BankFeedSyncSettingsSchema,
  }),

  triggerTransactionsExport: endpoint({
    method: 'POST',
    url: 'bank-feed/transactions/grid/export',
    body: BankFeedTransactionsExportBodySchema,
    response: BankFeedTransactionsExportResponseSchema,
  }),
};
